import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef(null);

  const scheduleTokenRefresh = useCallback(() => {
    // Refresh token 5 minutes before expiry (2 hour tokens = refresh at 115 min)
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        scheduleTokenRefresh(); // Schedule next refresh
      } catch {
        // Refresh failed - user needs to login again
        setUser(null);
      }
    }, 115 * 60 * 1000); // 115 minutes
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      scheduleTokenRefresh();
    } catch (error) {
      // If 401, try refreshing the token once
      if (error.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(response.data);
          scheduleTokenRefresh();
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [scheduleTokenRefresh]);

  useEffect(() => {
    checkAuth();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [checkAuth]);

  // Add axios interceptor for automatic token refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/register')
        ) {
          originalRequest._retry = true;
          try {
            await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
            return axios(originalRequest);
          } catch {
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    scheduleTokenRefresh();
    return response.data;
  };

  const register = async (email, password, name, demographics = {}, referralCode = null) => {
    const payload = { email, password, name, ...demographics };
    if (referralCode) payload.referral_code = referralCode;
    const response = await axios.post(`${API}/auth/register`,
      payload,
      { withCredentials: true }
    );
    setUser(response.data);
    scheduleTokenRefresh();
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      exchangeSession,
      logout,
      updateUser,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
